import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DataDocs } from "../../types/tables-data";

export type UploadConfiguration = {
  // individual id
  uid: string;
  // cloud storage path
  path: string;
  // Firestore path path
  collectionPath?: string;
  // mainDocument Id if present
  mainDocument?: DataDocs;
  // super class for upload by category
  super_class?: string;
  // category for upload by category
  category?: string;
  // type for upload by category
  type?: string;
  customType?: string;
  // source of files
  source?: "external-link" | "local" | "cloud";
};

export type FileUploadState = {
  files: File[];
  uploadProgress: { [key: string]: number };
  isLoading: boolean;
  visible: boolean;
  expanded: boolean;
  error: string | null;
  uploadConfiguration: UploadConfiguration;
  fileErrors: { [fileName: string]: string };
  externalLinkModal: {
    isOpen: boolean;
    storagePath: string;
    collectionPath: string;
    key: number;
  };
};
const initialState: FileUploadState = {
  files: [],
  uploadProgress: {},
  isLoading: false,
  error: null,
  visible: false,
  expanded: false,
  uploadConfiguration: {
    path: "",
    uid: ""
  },
  fileErrors: {},
  externalLinkModal: {
    isOpen: false,
    storagePath: "",
    collectionPath: "",
    key: 0
  }
};

const fileUploadSlice = createSlice({
  name: "fileUpload",
  initialState,
  reducers: {
    setUploadConfiguration: (
      state,
      action: PayloadAction<UploadConfiguration>
    ) => {
      const configuration = action.payload;
      state.uploadConfiguration = {
        ...state.uploadConfiguration,
        ...configuration
      };
    },
    setVisibilty: (state, action: PayloadAction<boolean>) => {
      state.visible = action.payload;
    },
    setExpanded: (state, action: PayloadAction<boolean>) => {
      state.expanded = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setFiles(state, action: PayloadAction<File[]>) {
      state.files = [...action.payload];
      state.isLoading = true;
    },
    clearFiles(state) {
      state.files = [];
      state.uploadProgress = {};
      state.isLoading = false;
      state.fileErrors = {};
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setUploadProgress: (
      state,
      action: PayloadAction<{ [key: string]: number }>
    ) => {
      state.uploadProgress = {
        ...state.uploadProgress,
        ...action.payload
      };
    },
    setFileError: (
      state,
      action: PayloadAction<{ fileName: string; error: string }>
    ) => {
      state.fileErrors[action.payload.fileName] = action.payload.error;
    },
    clearFileErrors: (state) => {
      state.fileErrors = {};
    },
    openExternalLinkModal: (
      state,
      action: PayloadAction<{
        storagePath?: string;
        collectionPath?: string;
        forceNew?: boolean;
      }>
    ) => {
      const { storagePath, collectionPath, forceNew } = action.payload || {};
      if (forceNew) state.externalLinkModal.key += 1;
      state.externalLinkModal.isOpen = true;
      state.externalLinkModal.storagePath =
        storagePath ?? state.uploadConfiguration.path ?? "";
      state.externalLinkModal.collectionPath =
        collectionPath ?? state.uploadConfiguration.collectionPath ?? "";
    },
    closeExternalLinkModal: (state) => {
      state.externalLinkModal.isOpen = false;
    }
  }
});

export const {
  setFiles,
  clearFiles,
  setVisibilty,
  setExpanded,
  setUploadProgress,
  setIsLoading,
  setError,
  setUploadConfiguration,
  setFileError,
  clearFileErrors,
  openExternalLinkModal,
  closeExternalLinkModal
} = fileUploadSlice.actions;

export default fileUploadSlice.reducer;
